import { Button } from "@mui/material";
import SelectItem from "./SelectItem";
import { Comune, HeatPumpProduct, ProductTypeId, SolarProduct, TerminalType, WaterHeaterProduct } from "../types/types";
import dataComuni from "../data/comuni-zona-climatiche.json";
import dataProductsPdc from "../data/products_pdc.json";
import dataScaldacqua from "../data/products_scaldacqua.json";
import dataSolarProducts from "../data/solar_products.json";
import dataSolarServices from "../data/solar_services.json";
import { HEAT_PUMP_PRODUCT_TYPES, PRODUCT_TYPE_OPTIONS, TERMINAL_LABEL_BY_ID, TERMINAL_OPTIONS } from "../constants/productTypes";

interface Props {
    disabled: boolean;
    selectedType: ProductTypeId | null;
    selectedProduct: string | null;
    selectedComune: string | null;
    selectedTerminal: TerminalType | null;
    selectedService: string | null;
    selectedPanels: string | null;
    setType: (data: ProductTypeId | null) => void;
    setProduct: (data: string | null) => void;
    setComune: (data: string | null) => void;
    setTerminal: (data: TerminalType | null) => void;
    setService: (data: string | null) => void;
    setPanels: (data: string | null) => void;
    setResults: () => void;
}

export default function Search({
    setType,
    setProduct,
    setComune,
    setTerminal,
    setResults,
    setService,
    setPanels,
    selectedType,
    selectedProduct,
    selectedComune,
    selectedTerminal,
    selectedPanels,
    selectedService,
    disabled
}: Props) {
    const listaComuni: Comune[] = dataComuni;
    const listaProductPdc: HeatPumpProduct[] = dataProductsPdc;
    const listaScaldacqua: WaterHeaterProduct[] = dataScaldacqua;
    const listaSolar: SolarProduct[] = dataSolarProducts;
    const isHeatPumpType = selectedType ? HEAT_PUMP_PRODUCT_TYPES.includes(selectedType) : false;
    const isHybridType = selectedType === ProductTypeId.IBRIDO_FACTORY || selectedType === ProductTypeId.IBRIDO_BIVALENTE_LT35 || selectedType === ProductTypeId.IBRIDO_BIVALENTE_GT35;
    const isSolarType = selectedType === ProductTypeId.SOLARE;

    const modelOptions = (() => {
        if (selectedType === ProductTypeId.PDC || isHybridType) {
            return listaProductPdc
                .filter((item) => item.hide === false && (!isHybridType || !item.modello.toLowerCase().includes("solarsplit")))
                .map((item) => item.modello);
        }
        if (selectedType === ProductTypeId.SCALDACQUA) {
            return listaScaldacqua.filter((item) => item.hide === false).map((item) => item.modello);
        }
        if (selectedType === ProductTypeId.SOLARE) {
            return listaSolar.filter((item) => item.hide === false).map((item) => item.modello);
        }
        return [];
    })();

    return (
        <div className='flex justify-center border md:border-solid border-[#D8D8D8] rounded-2xl md:mx-5 md:py-10'>
            <div className="flex flex-col items-center justify-center w-full max-w-[784px] px-5 gap-10">
                <div className="w-full md:text-center">
                    <h4 className='uppercase font-black'>CALCOLA L'INCENTIVO PER IL CONTO TERMICO</h4>
                </div>
                <div className='flex flex-col gap-6 md:gap-8 mb-10 w-full'>
                    <SelectItem
                        data={PRODUCT_TYPE_OPTIONS.map((item) => item.label)}
                        onChange={(value) => {
                            const selected = PRODUCT_TYPE_OPTIONS.find((item) => item.label === value);
                            setType(selected?.id ?? null);
                            setProduct(null);
                            setComune(null);
                            setTerminal(null);
                            setPanels(null);
                            setService(null);
                        }}
                        value={PRODUCT_TYPE_OPTIONS.find((item) => item.id === selectedType)?.label ?? null}
                        label="Apparecchio"
                        placeholder="Seleziona apparecchio"
                    />
                    {selectedType &&
                        <SelectItem
                        data={modelOptions}
                        onChange={value => {
                            setProduct(value);
                            setComune(null);
                            setTerminal(null);
                            setPanels(null);
                            setService(null);
                        }}
                        value={selectedProduct}
                        label="Modello"
                        placeholder="Seleziona modello"
                    />
                    }
                    {selectedProduct && isHeatPumpType &&
                        <>
                            <SelectItem
                                value={selectedComune}
                                data={listaComuni.map(i => i.nome)}
                                onChange={value => setComune(value)}
                                label="Comune"
                                placeholder="Seleziona comune"
                            />
                            <SelectItem
                                data={TERMINAL_OPTIONS.map((item) => item.label)}
                                onChange={(value) => {
                                    const selected = TERMINAL_OPTIONS.find((item) => item.label === value);
                                    setTerminal(selected?.id ?? null);
                                }}
                                value={selectedTerminal ? TERMINAL_LABEL_BY_ID[selectedTerminal] : null}
                                label="Terminali"
                                placeholder="Seleziona terminali"
                            />
                        </>
                    }
                    {selectedProduct && isSolarType &&
                        <>
                            <SelectItem
                                data={dataSolarServices.map(i => i.nome)}
                                value={selectedService}
                                onChange={value => setService(value)}
                                label="Servizio"
                                placeholder="Seleziona servizio"
                            />
                            <SelectItem
                                value={selectedPanels?.toString()}
                                data={Array.from({ length: 6 }, (_, i) => (i + 1).toString())}
                                onChange={value => setPanels(value)}
                                label="Pannelli"
                                placeholder="Seleziona n. pannelli"
                            />
                        </>
                    }
                </div>
                <Button
                    variant='contained'
                    onClick={e => setResults()}
                    disabled={disabled}
                    fullWidth
                    className='bg-blue disabled:bg-gray hover:bg-yellow hover:text-blue text-white rounded-none text-xl p-4 font-black w-full md:max-w-[320px] normal-case'
                >
                    Calcola
                </Button>
            </div>
        </div>
    );
}
